import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import DashboardLayout from "../../components/DashboardLayout";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  Heart, CalendarRange, Award, ScrollText, ArrowUpRight, MapPin,
} from "lucide-react";

export default function ParentDashboard() {
  const { user } = useAuth();
  const [child, setChild] = useState(null);
  const [regs, setRegs] = useState([]);
  const [certs, setCerts] = useState([]);
  const [papers, setPapers] = useState([]);

  const childIds = user?.child_delegate_ids || [];

  useEffect(() => {
    if (!childIds.length) return;
    const childId = childIds[0];
    api.get(`/v2/registrations?delegate_id=${childId}&limit=20`)
      .then((r) => {
        const list = r.data.items || [];
        setRegs(list);
        if (list[0]) {
          setChild({
            id: childId,
            name: list[0].delegate_name,
            email: list[0].delegate_email,
          });
        }
      })
      .catch(() => setRegs([]));
    api.get(`/v2/certificates?delegate_id=${childId}&limit=20`)
      .then((r) => setCerts(r.data.items || []))
      .catch(() => setCerts([]));
    api.get(`/v2/position-papers?limit=50`)
      .then((r) => setPapers((r.data.items || []).filter((p) => p.delegate_id === childId)))
      .catch(() => setPapers([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const firstReg = regs[0];

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            Parent Portal
          </p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
            Your delegate's journey
          </h1>
          <p className="mt-2 text-slate-500 text-sm">
            Follow your child's MUN experience — safely, quietly, cheering from the stands.
          </p>
        </motion.div>

        {!childIds.length && (
          <div className="mt-10 rounded-xl bg-white border-2 border-dashed border-slate-200 p-10 text-center">
            <Heart className="h-6 w-6 text-[#D4AF37] mx-auto" />
            <p className="mt-3 text-sm text-slate-500">
              No linked delegate. Ask the school administrator to connect your account.
            </p>
          </div>
        )}

        {childIds.length > 0 && (
          <>
            <Card className="mt-8 bg-[#0A192F] text-white border-[#D4AF37]/30">
              <CardContent className="p-8 grid md:grid-cols-3 gap-6 items-center">
                <div className="md:col-span-2">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                    Your delegate
                  </p>
                  <h2 className="mt-2 text-3xl font-serif text-white">{child?.name || "—"}</h2>
                  <p className="mt-1 text-sm text-slate-300">{child?.email || ""}</p>
                  {firstReg && (
                    <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-300">
                      <span className="flex items-center gap-2">
                        <CalendarRange className="h-4 w-4 text-[#D4AF37]" />
                        {firstReg.event_id?.replace("event-", "")}
                      </span>
                      <span className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-[#D4AF37]" />
                        {firstReg.status}
                      </span>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <Stat label="Events" value={regs.length} />
                  <Stat label="Papers" value={papers.length} />
                  <Stat label="Awards" value={certs.length} />
                </div>
              </CardContent>
            </Card>

            <div className="mt-8 grid lg:grid-cols-12 gap-6">
              <div className="lg:col-span-7">
                <h3 className="text-lg font-semibold tracking-tight text-[#0A192F]">Registrations</h3>
                <div className="mt-4 space-y-3">
                  {regs.map((r) => (
                    <div key={r.id} className="p-5 rounded-xl bg-white border border-slate-200" data-testid={`parent-reg-${r.id}`}>
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-[#0A192F]">{r.event_id?.replace("event-", "")}</p>
                        <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full ${r.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-[#D4AF37]/15 text-[#0A192F]"}`}>
                          {r.payment_status}
                        </span>
                      </div>
                      {r.allocated_country_id && (
                        <p className="text-xs text-slate-500 mt-1">
                          Allocated: {r.allocated_country_id?.replace("country-", "")}
                        </p>
                      )}
                    </div>
                  ))}
                  {regs.length === 0 && (
                    <p className="text-sm text-slate-500">No registrations yet.</p>
                  )}
                </div>
              </div>

              <div className="lg:col-span-5 space-y-6">
                <Card className="bg-white border-slate-200">
                  <CardContent className="p-6">
                    <Award className="h-5 w-5 text-[#D4AF37]" />
                    <p className="mt-2 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
                      Awards & certificates
                    </p>
                    <div className="mt-4 space-y-2">
                      {certs.map((c) => (
                        <div key={c.id} className="p-3 rounded-md bg-slate-50 border border-slate-200" data-testid={`parent-cert-${c.code}`}>
                          <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">{c.award_type}</p>
                          <p className="text-sm font-semibold text-[#0A192F]">{c.event_title}</p>
                          <p className="font-mono text-[10px] text-slate-500 mt-1">{c.code}</p>
                        </div>
                      ))}
                      {certs.length === 0 && (
                        <p className="text-sm text-slate-500">No awards yet.</p>
                      )}
                    </div>
                    <Link to="/dashboard/certificates">
                      <Button variant="outline" className="mt-4 w-full border-[#0F4C81] text-[#0F4C81]" data-testid="all-certs-button">
                        All certificates <ArrowUpRight className="h-4 w-4 ml-1" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>

                <Card className="bg-white border-slate-200">
                  <CardContent className="p-6">
                    <ScrollText className="h-5 w-5 text-[#D4AF37]" />
                    <p className="mt-2 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
                      Position papers
                    </p>
                    <div className="mt-4 space-y-2">
                      {papers.slice(0, 3).map((p) => (
                        <div key={p.id} className="p-3 rounded-md bg-slate-50 border border-slate-200" data-testid={`parent-paper-${p.id}`}>
                          <p className="text-sm font-semibold text-[#0A192F]">{p.topic}</p>
                          <p className="text-xs text-slate-500">{p.country_name} · {p.status}</p>
                          {p.ai_evaluation?.overall_score != null && (
                            <p className="mt-1 font-mono text-xs text-[#D4AF37] font-bold">
                              AI score: {p.ai_evaluation.overall_score}/100
                            </p>
                          )}
                        </div>
                      ))}
                      {papers.length === 0 && (
                        <p className="text-sm text-slate-500">No papers submitted yet.</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md bg-white/5 border border-white/10 p-3 text-center">
      <p className="text-[9px] uppercase tracking-[0.22em] text-slate-400 font-bold">{label}</p>
      <p className="mt-1 font-serif text-2xl text-[#D4AF37]">{value}</p>
    </div>
  );
}
